import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import { AppContext } from '../../components/shared/context';
import { months } from '../../components/shared/utils/months';
import { RELATORIO_ENDPOINT, v2 } from '../../constants/endpoints';

export const useRelatorio = () => {
  const { backendGet, backendGetBlob, backendPostBlob, backendPost } =
    Sebrae.useBackend();
  const { empresa } = React.useContext(AppContext);

  const buscarRelatorioLancamentoPorDia = React.useCallback(
    (cnpj, params) =>
      backendGet(`${v2}/relatorio/dia/${empresa?.id}?${params}&origem=FLUXO`),
    [backendGet, empresa],
  );

  const buscarRelatorioLancamentoPorExtrato = React.useCallback(
    (cnpj, params) =>
      backendGet(
        `${v2}/relatorio/extrato/${empresa?.id}?${params}&origem=FLUXO`,
      ),
    [backendGet, empresa],
  );

  const buscarRelatorioLancamentoPorFluxo = React.useCallback(
    (cnpj, params, mensal) =>
      backendGet(
        `${v2}/relatorio/fluxo/${
          empresa?.id
        }?${params}&mensal=${!!mensal}&origem=FLUXO`,
      ),
    [backendGet, empresa],
  );

  const downloadRelatorioDia = React.useCallback(
    (cnpj, dataInicio, dataFim) =>
      backendGetBlob(
        `${v2}/relatorio/dia/${empresa?.id}/download/?dataInicio=${dataInicio}&dataFim=${dataFim}`,
      ),
    [backendGetBlob, empresa],
  );

  const downloadRelatorioExtrato = React.useCallback(
    (cnpj, dataInicio, dataFim) =>
      backendGetBlob(
        `${v2}/relatorio/extrato/${empresa?.id}/download/?dataInicio=${dataInicio}&dataFim=${dataFim}`,
      ),
    [backendGetBlob, empresa],
  );

  const downloadRelatorioFluxo = React.useCallback(
    (cnpj, dataInicio, dataFim) =>
      backendGetBlob(
        `${v2}/relatorio/fluxo/${empresa?.id}/download/?dataInicio=${dataInicio}&dataFim=${dataFim}`,
      ),
    [backendGetBlob, empresa],
  );

  const relatorioDasn = React.useCallback(
    (empresaId, ano) =>
      backendGet(
        `${RELATORIO_ENDPOINT}/relatorio-dasn/${empresaId}?ano=${ano}&origem=FLUXO`,
      ),
    [backendGet],
  );

  const relatorioDasnDownload = React.useCallback(
    (empresaId, ano) =>
      backendGetBlob(
        `${RELATORIO_ENDPOINT}/relatorio-dasn/${empresaId}/download?ano=${ano}&origem=FLUXO`,
      ),
    [backendGetBlob],
  );

  const relatorioMensalMei = React.useCallback(
    (empresaId, ano, mes) =>
      backendGet(
        `${RELATORIO_ENDPOINT}/relatorio-mensal-mei/${empresaId}?mes=${mes}&ano=${ano}&origem=FLUXO`,
      ),
    [backendGet],
  );

  const relatorioMensalMeiDownload = React.useCallback(
    (empresaId, ano, mes) =>
      backendGetBlob(
        `${RELATORIO_ENDPOINT}/relatorio-mensal-mei/${empresaId}/download?mes=${mes}&ano=${ano}&origem=FLUXO`,
      ),
    [backendGetBlob],
  );

  const relatorioContato = React.useCallback(
    (empresaId, params) =>
      backendGet(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-contato/${empresaId}?${params}&origem=FLUXO`,
      ),
    [backendGet],
  );

  const relatorioContatoDownload = React.useCallback(
    (empresaId, params) =>
      backendGetBlob(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-contato/${empresaId}/download?${params}&origem=FLUXO`,
      ),
    [backendGetBlob],
  );

  const relatorioConta = React.useCallback(
    (empresaId, params) =>
      backendGet(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-conta/${empresaId}?${params}&origem=FLUXO`,
      ),
    [backendGet],
  );

  const relatorioContaDownload = React.useCallback(
    (empresaId, params) =>
      backendGetBlob(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-conta/${empresaId}/download?${params}&origem=FLUXO`,
      ),
    [backendGetBlob],
  );

  const relatorioCategoria = React.useCallback(
    (empresaId, params) =>
      backendGet(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-categoria/${empresaId}?${params}&origem=FLUXO`,
      ),
    [backendGet],
  );

  const relatorioCategoriaDownload = React.useCallback(
    (empresaId, params) =>
      backendGetBlob(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-categoria/${empresaId}/download?${params}&origem=FLUXO`,
      ),
    [backendGetBlob],
  );

  const relatorioCategoriaGraficosDownload = React.useCallback(
    (empresaId, params, body) =>
      backendPostBlob(
        `${RELATORIO_ENDPOINT}/relatorio-lancamento-categoria/${empresaId}/download?${params}&origem=FLUXO`,
        body,
      ),
    [backendPostBlob],
  );

  const getFiltrosPredefinidos = React.useCallback(
    (empresaId) =>
      backendGet(`${v2}/relatorio/${empresaId}/filtros-predefinidos`),
    [backendGet],
  );

  const salvarFiltroPredefinido = React.useCallback(
    (params) =>
      backendPost(
        `${v2}/relatorio/${empresa?.id}/filtros-predefinidos`,
        params,
      ),
    [backendPost, empresa],
  );

  const compartilharRelatorio = React.useCallback(
    (params) =>
      backendPost(`${v2}/relatorio/${empresa?.id}/compartilhar`, params),
    [backendPost, empresa],
  );

  const relatorioPublico = React.useCallback(
    (key) => backendGet(`v1/public/relatorio/${key}`),
    [backendGet],
  );

  return {
    buscarRelatorioLancamentoPorDia,
    buscarRelatorioLancamentoPorExtrato,
    buscarRelatorioLancamentoPorFluxo,
    compartilharRelatorio,
    downloadRelatorioDia,
    downloadRelatorioExtrato,
    downloadRelatorioFluxo,
    getFiltrosPredefinidos,
    relatorioDasn,
    relatorioMensalMei,
    relatorioDasnDownload,
    relatorioMensalMeiDownload,
    relatorioContato,
    relatorioConta,
    relatorioCategoria,
    relatorioContatoDownload,
    relatorioContaDownload,
    relatorioCategoriaDownload,
    relatorioCategoriaGraficosDownload,
    relatorioPublico,
    salvarFiltroPredefinido,
  };
};

export function getYearRange(startYear) {
  const currentYear = new Date().getFullYear();
  const yearRange = [];

  for (let year = currentYear; year >= startYear; year--) {
    yearRange.push(year);
  }
  return yearRange;
}

export function useRepportService() {
  const startYear = 2008;
  const years = getYearRange(startYear);

  const [tiposRelatorios] = React.useState(
    typeReport.map((type) => ({ id: type, label: type })),
  );

  const [meses] = React.useState(
    months.map((type, index) => ({ id: type, label: type })),
  );

  const [anos] = React.useState(
    years?.map((type) => ({ id: type.toString(), label: type.toString() })),
  );

  const [listaSituacoes] = React.useState(
    situacoes?.map((type) => ({ id: type, label: type })),
  );

  const [tiposLancamento] = React.useState(
    lancamentos?.map((type) => ({ id: type.id, label: type.label })),
  );
  return { tiposRelatorios, meses, anos, listaSituacoes, tiposLancamento };
}

export const typeReport = [
  'Selecione',
  'Diário',
  'Extrato',
  'Fluxo de caixa',
  'Relatório DASN MEI',
  'Relatório Mensal MEI',
  'Relatório por contato',
  'Relatório por categoria',
  'Relatório por conta',
];

export const situacoes = [
  'A vencer',
  'Vence hoje',
  'Vencido',
  'Pago',
  'Atrasado',
  'Recebido',
];

export const lancamentos = [
  { id: 'D', label: 'Despesas' },
  { id: 'R', label: 'Receitas' },
];

export function arrayTagsFrase(array) {
  const labels = array?.map((item) => item.label) ?? [];
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return labels.join(' e ');

  const ultimoElemento = labels.pop();
  return `${labels.join(', ')} e ${ultimoElemento}`;
}

export const listFilterSituacao = [
  { id: 'A_VENCER', label: 'A vencer' },
  { id: 'VENCE_HOJE', label: 'Vence hoje' },
  { id: 'VENCIDO', label: 'Vencido' },
  { id: 'PAGO', label: 'Pago' },
  { id: 'ATRASADO', label: 'Atrasado' },
  { id: 'RECEBIDO', label: 'Recebido' },
];
